import { Theme, alpha } from "@mui/material/styles";

// Topographic line art comes in two variants. Each is chosen to contrast with
// the canvas it sits on: the dark-line art on the light (grey) canvas, the
// light-line art on the dark canvas — otherwise the lines wash out.
import linesSectionDarkBg from "../assets/lines-section-dark.webp";
import linesSectionLightBg from "../assets/lines-section-light.webp";

/**
 * Per the Figma design the texture sits at 20% opacity over the canvas. We can't
 * set opacity on a CSS background image directly, so we wash the canvas color
 * back over it at (1 - 0.2): a same-color veil at alpha 0.8 is mathematically
 * identical to rendering the image at opacity 0.2 over that color.
 */
const TEXTURE_OPACITY = 0.2;
const TEXTURE_VEIL_OPACITY = 1 - TEXTURE_OPACITY;

const baseSx = {
  backgroundRepeat: "no-repeat",
  // Corner accent: anchor the art's top-right to the viewport top-right and size
  // explicitly so we don't crop/zoom with `cover`.
  backgroundPosition: "right top",
  backgroundSize: "min(72vw, 1440px) auto",
} as const;

/**
 * Canvas tint for the topo veil. Always `palette.canvas` so every home surface
 * paints the same texture.
 */
export function homeLinesVeilColor(theme: Theme): string {
  return theme.palette.canvas;
}

type HomeLinesBackgroundOptions = {
  /**
   * Lock the art to the viewport. Required on the sticky home AppBar so the
   * header texture stays aligned with `HomeLinesBackdrop` while page content
   * scrolls underneath instead of showing through a transparent bar.
   */
  attachment?: "fixed" | "scroll";
};

/**
 * Topographic texture for the home backdrop. Matches the Figma "Content Area"
 * background: corner art at 20% opacity over the canvas color.
 */
export function homeLinesBackgroundSx(
  themeMode: "light" | "dark",
  canvasColor: string,
  options?: HomeLinesBackgroundOptions
) {
  const image = themeMode === "dark" ? linesSectionLightBg : linesSectionDarkBg;
  const veil = alpha(canvasColor, TEXTURE_VEIL_OPACITY);
  return {
    ...baseSx,
    ...(options?.attachment === "fixed" ? { backgroundAttachment: "fixed" } : {}),
    backgroundImage: `linear-gradient(${veil}, ${veil}), url(${image})`,
  };
}
